import { Fragment, type ReactNode } from 'react';
import { emojiOnly, parseRichText, type Inline } from '@machs/shared';

/**
 * Nachrichtentext mit den Formatierungen aus dem Messenger (*fett*, _kursiv_,
 * ~durchgestrichen~, `Code`, Blöcke, Zitate, Listen) und klickbaren Links.
 * Der Parser liefert einen Baum; hier entsteht daraus React, nie rohes HTML.
 */
export function RichText({ text, mentions = [], me }: {
  text: string;
  /** Erwähnte Personen dieser Nachricht; nur sie werden hervorgehoben */
  mentions?: { id: string; name: string }[];
  /** Eigene ID: die eigene Erwähnung sticht stärker hervor */
  me?: string;
}) {
  const jumbo = emojiOnly(text);
  if (jumbo) return <p className={`rt-jumbo n${jumbo}`}>{text.replace(/\s+/g, '')}</p>;
  const inline = (nodes: Inline[]) => renderInline(nodes, me);
  const lines = (ls: Inline[][]) => renderLines(ls, me);
  return (
    <div className="rt">
      {parseRichText(text, { mentions }).map((b, i) => {
        switch (b.t) {
          case 'pre':
            return <pre key={i}>{b.v}</pre>;
          case 'quote':
            return <blockquote key={i}>{lines(b.lines)}</blockquote>;
          case 'ul':
            return <ul key={i}>{b.items.map((it, j) => <li key={j}>{inline(it)}</li>)}</ul>;
          case 'ol':
            return <ol key={i} start={b.start}>{b.items.map((it, j) => <li key={j}>{inline(it)}</li>)}</ol>;
          default:
            return <p key={i}>{lines(b.lines)}</p>;
        }
      })}
    </div>
  );
}

function renderLines(ls: Inline[][], me?: string): ReactNode {
  return ls.map((l, i) => (
    <Fragment key={i}>
      {i > 0 && <br />}
      {renderInline(l, me)}
    </Fragment>
  ));
}

function renderInline(nodes: Inline[], me?: string): ReactNode {
  return nodes.map((n, i) => {
    switch (n.t) {
      case 'b':
        return <strong key={i}>{renderInline(n.c, me)}</strong>;
      case 'i':
        return <em key={i}>{renderInline(n.c, me)}</em>;
      case 's':
        return <s key={i}>{renderInline(n.c, me)}</s>;
      case 'mention':
        return <span key={i} className={`rt-mention ${n.id === me ? 'me' : ''}`}>{n.v}</span>;
      case 'code':
        return <code key={i}>{n.v}</code>;
      case 'link':
        return (
          <a key={i} href={n.href} target={n.href.startsWith('mailto:') ? undefined : '_blank'} rel="noreferrer noopener" onClick={(e) => e.stopPropagation()}>
            {n.v}
          </a>
        );
      default:
        return <Fragment key={i}>{n.v}</Fragment>;
    }
  });
}
